import { FC, useMemo, useState } from "react";
import { Box, IconButton, Link, Stack, Typography } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { IContent } from "../../../models/admin/cms/contentTypes";

const DISMISSED_ALERTS_KEY = "dismissedAlerts";

interface ICMSAlertBannerProps {
  cmsAlerts: IContent[];
}

const CMSAlertBanner: FC<ICMSAlertBannerProps> = ({ cmsAlerts = [] }) => {
  const [dismissedAlerts, setDismissedAlerts] = useState<string[]>(() => {
    // Initialize state from localStorage
    const saved = localStorage.getItem(DISMISSED_ALERTS_KEY);
    return saved ? JSON.parse(saved) : [];
  });

  const publishedAlerts = useMemo(() => {
    const today = new Date();
    const startOfDay = new Date(today.setHours(0, 0, 0, 0));
    const endOfDay = new Date(today.setHours(23, 59, 59, 999));
    return cmsAlerts?.filter(
      (item) =>
        item?.contentTypeName?.toLowerCase().includes("alert") &&
        item?.status?.toLowerCase() === "published" &&
        item?.effectiveStartDate &&
        item?.effectiveEndDate &&
        new Date(item?.effectiveStartDate) <= endOfDay &&
        new Date(item?.effectiveEndDate) >= startOfDay &&
        !dismissedAlerts.includes(item.id)
    );
  }, [cmsAlerts, dismissedAlerts]);

  const handleDismiss = (alertId: string) => {
    const newDismissedAlerts = [...dismissedAlerts, alertId];
    setDismissedAlerts(newDismissedAlerts);
    // Save to localStorage
    localStorage.setItem(
      DISMISSED_ALERTS_KEY,
      JSON.stringify(newDismissedAlerts)
    );
  };

  if (!publishedAlerts?.length) return null;

  return (
    <Stack
      spacing={1}
      sx={{
        width: "100%",
        padding: "0.5rem 2rem",
      }}
    >
      {publishedAlerts?.map((alert) => (
        <Box
          key={alert.id}
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "12px 24px",
            backgroundColor: alert.fields["Banner Color"] || "#f44336",
            color: "#fff",
            borderRadius: "4px",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Typography>{alert.fields["Banner Text"]}</Typography>
            {alert.fields["Redirect URL"] && (
              <Link
                href={alert.fields["Redirect URL"]}
                sx={{ color: "inherit", textDecoration: "underline" }}
                target="_blank"
                rel="noopener"
              >
                {alert.fields["Link Label"]}
              </Link>
            )}
          </Box>
          <IconButton
            size="small"
            onClick={() => handleDismiss(alert.id)}
            sx={{ color: "inherit" }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      ))}
    </Stack>
  );
};

export default CMSAlertBanner;
